import { useState, useMemo } from "react";
import Layout from "@/layouts/layout";
import type { Project } from "@/interfaces/project";
import ProjectCard from "@/components/projects/ProjectCard";
import PageHeading from "@/components/layout/PageHeading";
import Panel from "@/components/ui/Panel";
import Badge from "@/components/ui/Badge";
import EmptyState from "@/components/ui/EmptyState";
import { Input } from "@/components/ui/Field";
import { Link } from "@inertiajs/react";
import formatTime from "@/utils/formatTime";

type QueueRow = Project & {
  username: string;
  event_name: string | null;
  ticket_count?: number;
};

interface Props {
  queue: (Project & { username: string; event_name: string | null })[];
  all_queued: QueueRow[];
  hq_queued: QueueRow[];
  queue_count: number;
  hq_queue_count: number;
  can_authorize: boolean;
  week_leaderboard: { id: number; name: string; count: number }[];
  alltime_leaderboard: { id: number; name: string; count: number }[];
}

const TH =
  "font-display text-caption text-titlebar-ink px-3 py-2 text-left font-bold tracking-wide uppercase";
const TD = "text-caption text-ink-muted px-3 py-2 align-middle";

// Whole-row links: every cell wraps the same href so a click anywhere in the
// row opens the project, without nesting a <tr> inside an <a>.
function Cell({
  id,
  children,
  className,
}: {
  id: number;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <td className={`${TD} ${className ?? ""}`}>
      <Link href={`/review/${id}`} className="block">
        {children}
      </Link>
    </td>
  );
}

function QueueTable({
  rows,
  emptyLabel,
  showScreenshot = false,
  showTickets = false,
}: {
  rows: QueueRow[];
  emptyLabel: string;
  showScreenshot?: boolean;
  showTickets?: boolean;
}) {
  // #, Title, Author, Shipped for, Submitted, Reported, plus the optional ones.
  const columns = 6 + (showScreenshot ? 1 : 0) + (showTickets ? 2 : 0);

  return (
    <div className="border-line max-h-[600px] overflow-auto border-2">
      <table className="w-full border-collapse">
        <thead className="bg-titlebar sticky top-0 z-10">
          <tr>
            <th className={TH}>#</th>
            {showScreenshot && <th className={TH}>Shot</th>}
            <th className={TH}>Title</th>
            <th className={TH}>Author</th>
            <th className={TH}>Shipped for</th>
            <th className={TH}>Submitted</th>
            <th className={TH}>Reported</th>
            {showTickets && <th className={TH}>Prior</th>}
            {showTickets && <th className={TH}>Tickets</th>}
          </tr>
        </thead>
        <tbody className="bg-surface">
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns}
                className="text-body text-ink-soft px-3 py-10 text-center italic"
              >
                {emptyLabel}
              </td>
            </tr>
          ) : (
            rows.map((project, index) => (
              <tr
                key={project.id}
                className="border-line/20 hover:bg-goldenrod/20 duration-fast border-b transition-colors last:border-b-0"
              >
                <Cell id={project.id} className="text-ink-soft">
                  {index + 1}
                </Cell>
                {showScreenshot && (
                  <Cell id={project.id}>
                    {project.screenshot ? (
                      <img
                        src={project.screenshot}
                        alt=""
                        className="border-line h-10 w-16 border object-cover"
                      />
                    ) : (
                      <div className="border-line/40 bg-surface-alt text-caption text-ink-soft flex h-10 w-16 items-center justify-center border">
                        -
                      </div>
                    )}
                  </Cell>
                )}
                <Cell id={project.id} className="text-ink font-bold">
                  <span className="hover:underline">{project.title}</span>
                </Cell>
                <Cell id={project.id}>{project.username}</Cell>
                <Cell id={project.id}>
                  {project.event_name ?? (
                    <span className="text-ink-soft italic">travel grant</span>
                  )}
                </Cell>
                <Cell id={project.id}>
                  {project.submitted_at
                    ? new Date(project.submitted_at).toLocaleDateString()
                    : "-"}
                </Cell>
                <Cell id={project.id}>
                  {formatTime(project.reported_seconds)}
                </Cell>
                {showTickets && (
                  <Cell id={project.id}>
                    {project.real_approved_seconds > 0
                      ? formatTime(project.real_approved_seconds)
                      : "-"}
                  </Cell>
                )}
                {showTickets && (
                  <Cell id={project.id}>{project.ticket_count ?? 0}</Cell>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

const PODIUM = ["bg-goldenrod", "bg-viking", "bg-carrot-orange"] as const;

function Leaderboard({
  title,
  users,
}: {
  title: string;
  users: { id: number; name: string; count: number }[];
}) {
  return (
    <div className="flex flex-col gap-2">
      <h3 className="font-display text-subheading text-ink font-bold">
        {title}
      </h3>
      {users.length === 0 ? (
        <p className="text-caption text-ink-soft italic">No reviews yet.</p>
      ) : (
        <ol className="flex flex-col gap-2">
          {users.map((user, index) => (
            <li
              key={user.id}
              className={`border-line flex items-center gap-3 border-2 px-4 py-2.5 ${
                PODIUM[index] ?? "bg-surface-alt"
              }`}
            >
              <span className="font-display text-subheading text-ink w-6 font-bold">
                {index + 1}
              </span>
              <span className="font-display text-body text-ink min-w-0 flex-1 truncate font-bold">
                {user.name}
              </span>
              <span className="text-caption text-ink-muted shrink-0">
                {user.count} review{user.count !== 1 && "s"}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export default function Review({
  queue,
  all_queued,
  hq_queued,
  queue_count,
  hq_queue_count,
  can_authorize,
  week_leaderboard,
  alltime_leaderboard,
}: Props) {
  const [search, setSearch] = useState("");

  const weekSorted = [...week_leaderboard].sort((a, b) => b.count - a.count);
  const alltimeSorted = [...alltime_leaderboard].sort(
    (a, b) => b.count - a.count,
  );

  const filteredQueue = useMemo(() => {
    if (!search.trim()) return all_queued;
    const term = search.toLowerCase().trim();
    return all_queued.filter(
      (project) =>
        project.title?.toLowerCase().includes(term) ||
        project.username?.toLowerCase().includes(term) ||
        project.event_name?.toLowerCase().includes(term),
    );
  }, [all_queued, search]);

  return (
    <Layout>
      <div className="flex flex-col gap-8">
        <PageHeading
          title="Review"
          subtitle="Approved hours pay into the event a project was shipped for, so read them carefully."
        />

        {queue.length > 0 && (
          <section className="flex flex-col gap-3">
            <h2 className="font-display text-heading text-ink font-bold">
              Next up
            </h2>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {queue.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  link={`/review/${project.id}`}
                />
              ))}
            </div>
          </section>
        )}

        <Panel title={`queue.csv - ${queue_count} waiting`} bodyClassName="p-0">
          <div className="border-line/20 border-b p-4">
            <Input
              type="search"
              placeholder="Search by title, author or event"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-sm"
            />
          </div>
          {all_queued.length === 0 ? (
            <div className="p-4">
              <EmptyState
                title="Queue's empty"
                description="Nothing is waiting on a reviewer right now."
              />
            </div>
          ) : (
            <QueueTable
              rows={filteredQueue}
              emptyLabel="No projects match your search."
              showScreenshot
              showTickets
            />
          )}
        </Panel>

        {hq_queued.length > 0 && (
          <Panel
            title={`hq-authorization.csv - ${hq_queue_count}`}
            tone="alt"
            bodyClassName="p-0"
          >
            <div className="border-line/20 flex flex-wrap items-center gap-3 border-b p-4">
              <Badge tone="warning">Held for HQ</Badge>
              <p className="text-caption text-ink-muted">
                {can_authorize
                  ? "Read the history on each approval, then authorize or discard it."
                  : "Waiting for an HQ reviewer to authorize them."}
              </p>
            </div>
            <QueueTable rows={hq_queued} emptyLabel="Nothing held." />
          </Panel>
        )}

        <Panel title="leaderboard.txt" tone="alt">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <Leaderboard title="Last week" users={weekSorted} />
            <Leaderboard title="All time" users={alltimeSorted} />
          </div>
        </Panel>
      </div>
    </Layout>
  );
}
